import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ResearchOverview from './components/ResearchOverview';
import MonthlyLogs from './components/MonthlyLogs';
import ResearchDesigns from './components/ResearchDesigns';
import ResearchNotes from './components/ResearchNotes';
import FigureGallery from './components/FigureGallery';
import ResearchOutputs from './components/ResearchOutputs';
import ResearchGuide from './components/ResearchGuide';
import LogThemeSummary from './components/LogThemeSummary';
import MilestoneCelebration from './components/MilestoneCelebration';
import MonthlyLogForm from '../admin/components/MonthlyLogForm';
import LiteratureNoteForm from '../admin/components/LiteratureNoteForm';
import ResearchDesignForm from '../admin/components/ResearchDesignForm';
import ResearchFigureForm from '../admin/components/ResearchFigureForm';
import TermDefinitionForm from '../admin/components/TermDefinitionForm';
import ResearchOutputForm from '../admin/components/ResearchOutputForm';
import ResearchOverviewForm from '../admin/components/ResearchOverviewForm';
import ResearchProcedureForm from '../admin/components/ResearchProcedureForm';
import { exportResearchToPDF } from '../../utils/pdfExport';

// ─── モチベーションバナー ─────────────────────────────────────────────────────
function MotivationBanner({ logs }: { logs: any[] }) {
  const [visible, setVisible] = useState(true);
  const [sparkle, setSparkle] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setSparkle(v => !v), 3000);
    return () => clearInterval(timer);
  }, []);

  if (!visible || logs.length === 0) return null;

  const totalLogs = logs.length;
  const avgAch = Math.round(logs.reduce((s, l) => s + (l.achievement_rate ?? 0), 0) / totalLogs);
  const avgProg = Math.round(logs.reduce((s, l) => s + (l.progress_rate ?? 0), 0) / totalLogs);

  const getMessage = () => {
    const avg = (avgAch + avgProg) / 2;
    if (avg >= 80) return { text: '素晴らしい！研究が順調に進んでいます 🔥', sub: 'この調子で突き進もう！' };
    if (avg >= 60) return { text: '着実に前進中！継続は力なり 💪', sub: 'コツコツ積み上げた記録が財産になります' };
    if (avg >= 40) return { text: '一歩一歩、確実に進んでいます 🌱', sub: '小さな進歩も大切な記録です' };
    return { text: '記録することが研究の第一歩！ ✨', sub: '今日の気づきを書き留めましょう' };
  };

  const msg = getMessage();

  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-600 rounded-2xl p-5 mb-6 text-white shadow-lg">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="absolute rounded-full bg-white/10"
            style={{ width: `${40 + i * 20}px`, height: `${40 + i * 20}px`, top: `${10 + i * 12}%`, left: `${5 + i * 15}%`, animation: `float ${4 + i}s ease-in-out infinite`, animationDelay: `${i * 0.5}s` }}
          ></div>
        ))}
      </div>
      <button onClick={() => setVisible(false)} className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer">
        <i className="ri-close-line text-sm"></i>
      </button>
      <div className="relative flex items-center gap-4">
        <div className={`w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0 transition-transform duration-500 ${sparkle ? 'scale-110 rotate-6' : 'scale-100 rotate-0'}`}>
          <i className="ri-rocket-2-line text-2xl"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-base">{msg.text}</p>
          <p className="text-teal-100 text-sm mt-0.5">{msg.sub}</p>
        </div>
        <div className="hidden sm:flex items-center gap-4 flex-shrink-0">
          <div className="text-center"><p className="text-2xl font-bold">{totalLogs}</p><p className="text-teal-100 text-xs">記録数</p></div>
          <div className="text-center"><p className="text-2xl font-bold">{avgAch}%</p><p className="text-teal-100 text-xs">平均達成度</p></div>
          <div className="text-center"><p className="text-2xl font-bold">{avgProg}%</p><p className="text-teal-100 text-xs">平均進捗度</p></div>
        </div>
      </div>
    </div>
  );
}

// ─── 最近の投稿タイムライン ───────────────────────────────────────────────────
function RecentActivityTimeline({ activities }: { activities: any[] }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 mb-8 shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl">
          <i className="ri-time-line text-white text-lg"></i>
        </div>
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">最近の投稿</h2>
      </div>
      {activities.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">まだ投稿がありません</p>
      ) : (
        <div className="space-y-3">
          {activities.map((activity, index) => (
            <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors border border-transparent hover:border-teal-200 dark:hover:border-teal-700">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 dark:bg-teal-900/40 rounded-lg flex-shrink-0">
                <i className={`${activity.icon} text-teal-600 dark:text-teal-400`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 whitespace-nowrap">{activity.type}</span>
                  {activity.theme && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700 whitespace-nowrap flex items-center gap-1">
                      <i className="ri-lightbulb-line text-xs"></i>{activity.theme}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{activity.title}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{activity.date}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── 進捗サマリーカード ───────────────────────────────────────────────────────
function ProgressSummaryCard({
  data, onItemClick,
}: {
  data: { allOverviews: any[]; logs: any[]; literatureNotes: any[]; designs: any[]; figures: any[]; termDefinitions: any[]; outputs: any[] };
  onItemClick: (type: 'overview' | 'log' | 'note' | 'design' | 'figure' | 'term' | 'output') => void;
}) {
  const items: { label: string; count: number; icon: string; color: string; type: 'overview' | 'log' | 'note' | 'design' | 'figure' | 'term' | 'output' }[] = [
    { label: 'テーマと目的', count: data.allOverviews.length, icon: 'ri-lightbulb-line', color: 'from-yellow-500 to-orange-500', type: 'overview' },
    { label: '進捗ログ', count: data.logs.length, icon: 'ri-bar-chart-line', color: 'from-teal-500 to-cyan-500', type: 'log' },
    { label: '参考文献・用語集', count: data.literatureNotes.length + data.termDefinitions.length, icon: 'ri-book-line', color: 'from-emerald-500 to-teal-500', type: 'note' },
    { label: '分析方法', count: data.designs.length, icon: 'ri-flask-line', color: 'from-green-500 to-emerald-500', type: 'design' },
    { label: '図表', count: data.figures.length, icon: 'ri-pie-chart-line', color: 'from-teal-500 to-green-500', type: 'figure' },
    { label: '提出物', count: data.outputs.length, icon: 'ri-folder-download-line', color: 'from-cyan-500 to-teal-500', type: 'output' },
  ];
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
      <div className="flex items-center gap-3 mb-4 sm:mb-5">
        <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl">
          <i className="ri-dashboard-line text-white text-base sm:text-lg"></i>
        </div>
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white">研究進捗サマリー</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">各項目をクリックして投稿できます</p>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        {items.map(({ label, count, icon, color, type }) => (
          <button key={label} onClick={() => onItemClick(type)}
            className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-2 sm:p-4 text-center hover:shadow-lg hover:scale-105 transition-all group cursor-pointer"
          >
            <div className={`w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br ${color} rounded-lg sm:rounded-xl mx-auto mb-1 sm:mb-2 group-hover:scale-110 transition-transform shadow-md`}>
              <i className={`${icon} text-white text-sm sm:text-xl`}></i>
            </div>
            <p className="text-lg sm:text-2xl font-bold text-gray-800 dark:text-white mb-0.5 sm:mb-1">{count}</p>
            <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 font-medium leading-tight">{label}</p>
            <p className="text-[9px] sm:text-[10px] text-teal-500 dark:text-teal-400 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">クリックして投稿</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── 投稿パネル ───────────────────────────────────────────────────────────────
function PostPanel({ postType, setPostType, editingItem, onSuccess, onClose }: {
  postType: string; setPostType: (t: any) => void; editingItem: any; onSuccess: () => void; onClose: () => void;
}) {
  const postTypes = [
    { type: 'overview' as const, label: 'テーマと目的', icon: 'ri-lightbulb-line', color: 'from-yellow-500 to-orange-500' },
    { type: 'procedure' as const, label: '研究手順', icon: 'ri-route-line', color: 'from-indigo-500 to-violet-500' },
    { type: 'log' as const, label: '進捗ログ', icon: 'ri-bar-chart-line', color: 'from-teal-500 to-cyan-500' },
    { type: 'note' as const, label: '参考文献', icon: 'ri-article-line', color: 'from-emerald-500 to-teal-500' },
    { type: 'design' as const, label: '分析方法', icon: 'ri-flask-line', color: 'from-green-500 to-emerald-500' },
    { type: 'figure' as const, label: '図表', icon: 'ri-pie-chart-line', color: 'from-teal-500 to-green-500' },
    { type: 'term' as const, label: '用語集', icon: 'ri-book-mark-line', color: 'from-emerald-600 to-teal-600' },
    { type: 'output' as const, label: '提出物', icon: 'ri-folder-download-line', color: 'from-cyan-500 to-teal-500' },
  ];

  return (
    <div id="post-panel" className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-6 sm:mb-8 shadow-lg">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-800 dark:text-white">
          {editingItem ? '投稿を編集する' : '研究を記録する'}
        </h2>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer">
          <i className="ri-close-line text-xl"></i>
        </button>
      </div>
      
      {!editingItem && (
        <div className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3 mb-4 sm:mb-6">
          {postTypes.map(({ type, label, icon, color }) => (
            <button key={type} onClick={() => setPostType(type)}
              className={`p-2 sm:p-3 rounded-xl border-2 transition-all cursor-pointer ${postType === type ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 shadow-md' : 'border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-600'}`}
            >
              <div className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-gradient-to-br ${color} rounded-lg mx-auto mb-1 sm:mb-2`}>
                <i className={`${icon} text-white text-sm sm:text-base`}></i>
              </div>
              <p className="text-[9px] sm:text-[10px] font-semibold text-gray-800 dark:text-white leading-tight text-center">{label}</p>
            </button>
          ))}
        </div>
      )}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 sm:p-6">
        {postType === 'overview' && <ResearchOverviewForm onClose={onClose} onSuccess={onSuccess} editData={editingItem} />}
        {postType === 'procedure' && <ResearchProcedureForm onSuccess={onSuccess} />}
        {postType === 'log' && <MonthlyLogForm onClose={onClose} onSuccess={onSuccess} editData={editingItem} />}
        {postType === 'note' && <LiteratureNoteForm onClose={onClose} onSuccess={onSuccess} editData={editingItem} />}
        {postType === 'design' && <ResearchDesignForm onClose={onClose} onSuccess={onSuccess} editData={editingItem} />}
        {postType === 'figure' && <ResearchFigureForm onClose={onClose} onSuccess={onSuccess} editData={editingItem} />}
        {postType === 'term' && <TermDefinitionForm onClose={onClose} onSuccess={onSuccess} editData={editingItem} />}
        {postType === 'output' && <ResearchOutputForm onClose={onClose} onSuccess={onSuccess} editData={editingItem} />}
      </div>
    </div>
  );
}

// ─── 研究概要削除確認モーダル ─────────────────────────────────────────────────
function DeleteOverviewModal({ overview, onConfirm, onCancel }: {
  overview: any; onConfirm: (deleteRelated: boolean) => void; onCancel: () => void;
}) {
  const [deleteRelated, setDeleteRelated] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 flex items-center justify-center bg-rose-100 dark:bg-rose-900/40 rounded-full flex-shrink-0">
            <i className="ri-alert-line text-rose-600 dark:text-rose-400 text-xl"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800 dark:text-white">テーマと目的を削除</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">この操作は取り消せません</p>
          </div>
        </div>
        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-700 rounded-xl p-4 mb-4">
          <p className="text-sm font-semibold text-rose-800 dark:text-rose-300 mb-1">削除対象：</p>
          <p className="text-sm text-rose-700 dark:text-rose-400 font-bold">「{overview?.title}」</p>
        </div>
        {/* 進捗ログも削除するオプション */}
        <label className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl mb-5 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors">
          <input
            type="checkbox"
            checked={deleteRelated}
            onChange={e => setDeleteRelated(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-rose-600 cursor-pointer flex-shrink-0"
          />
          <div>
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">このテーマの進捗ログも同時に削除する</p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
              チェックを入れると、このテーマに紐づく進捗ログがすべて削除されます。
            </p>
          </div>
        </label>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            キャンセル
          </button>
          <button onClick={() => onConfirm(deleteRelated)} className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            削除する
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── テーマ別表示 ─────────────────────────────────────────────────────────────
function ThemeBasedView({
  allOverviews, logs, literatureNotes, designs, figures, termDefinitions, outputs,
  currentUser, isAdmin, onUpdate,
  onEditOverview, onDeleteOverview, onEditLog, onDeleteLog,
  onEditLiterature, onDeleteLiterature,
  onEditDesign, onDeleteDesign,
  onEditFigure, onDeleteFigure,
  onEditTerm, onDeleteTerm,
  onEditOutput, onDeleteOutput,
  onAddLog,
  onOpenProcedurePanel,
}: {
  allOverviews: any[]; logs: any[]; literatureNotes: any[]; designs: any[];
  figures: any[]; termDefinitions: any[]; outputs: any[];
  currentUser: any; isAdmin: boolean; onUpdate: () => void;
  onEditOverview: (item: any) => void;
  onDeleteOverview: (item: any) => void;
  onEditLog: (item: any) => void; onDeleteLog: (id: string) => void;
  onEditLiterature: (item: any) => void; onDeleteLiterature: (id: string) => void;
  onEditDesign: (item: any) => void; onDeleteDesign: (id: string) => void;
  onEditFigure: (item: any) => void; onDeleteFigure: (id: string) => void;
  onEditTerm: (item: any) => void; onDeleteTerm: (id: string) => void;
  onEditOutput: (item: any) => void; onDeleteOutput: (id: string) => void;
  onAddLog: () => void;
  onOpenProcedurePanel: () => void;
}) {
  const [exportingTheme, setExportingTheme] = useState<string | null>(null);
  const [showInlineProcedure, setShowInlineProcedure] = useState(false);

  const handleExportPDF = async (overview: any, theme: string) => {
    try {
      setExportingTheme(theme);
      await exportResearchToPDF(theme, {
        overview,
        logs: logs.filter(item => item.research_theme === theme),
        designs: designs.filter(item => item.research_theme === theme),
        literatureNotes: literatureNotes.filter(item => item.research_theme === theme),
        figures: figures.filter(item => item.research_theme === theme),
        termDefinitions: termDefinitions.filter(item => item.research_theme === theme),
        outputs: outputs.filter(item => item.research_theme === theme),
      });
    } catch (error) {
      console.error('PDFエクスポートエラー:', error);
      alert('PDFのエクスポートに失敗しました。もう一度お試しください。');
    } finally {
      setExportingTheme(null);
    }
  };

  if (allOverviews.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-12 text-center">
        <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="ri-folder-open-line text-4xl text-gray-400"></i>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-lg">まだテーマが登録されていません</p>
        <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">「テーマと目的」を登録して研究を始めましょう</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {logs.length > 0 && (
        <LogThemeSummary logs={logs} onThemeClick={onAddLog} />
      )}

      {allOverviews.map((overview) => {
        const theme = overview.title || '（テーマ名なし）';
        const themeLogs = logs.filter(item => item.research_theme === theme);
        const themeLiteratureNotes = literatureNotes.filter(item => item.research_theme === theme);
        const themeDesigns = designs.filter(item => item.research_theme === theme);
        const themeFigures = figures.filter(item => item.research_theme === theme);
        const themeTermDefinitions = termDefinitions.filter(item => item.research_theme === theme);
        const themeOutputs = outputs.filter(item => item.research_theme === theme);
        const totalPosts = themeLogs.length + themeLiteratureNotes.length + themeDesigns.length + themeFigures.length + themeTermDefinitions.length + themeOutputs.length;
        const isExporting = exportingTheme === theme;
        const canDelete = isAdmin || (currentUser && overview.user_id === currentUser.id);

        return (
          <div key={overview.id} className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 overflow-hidden shadow-lg">
            {/* テーマヘッダー */}
            <div className="bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-600 px-6 py-6 text-white">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
                      <i className="ri-lightbulb-line text-lg"></i>
                    </div>
                    <span className="text-teal-100 text-xs font-medium uppercase tracking-wider">研究テーマ</span>
                  </div>
                  <h2 className="text-2xl font-bold mb-2">{theme}</h2>
                  <div className="flex items-center gap-4 text-sm text-teal-100">
                    <span className="flex items-center gap-1"><i className="ri-file-list-3-line"></i>{totalPosts}件の投稿</span>
                    <span className="flex items-center gap-1"><i className="ri-time-line"></i>最終更新: {new Date(overview.updated_at || overview.created_at).toLocaleDateString('ja-JP')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  {/* 削除ボタン（投稿者本人または管理者のみ） */}
                  {canDelete && (
                    <button
                      onClick={() => onDeleteOverview(overview)}
                      className="flex items-center gap-2 px-3 py-2 bg-rose-500/80 hover:bg-rose-600 backdrop-blur-sm rounded-lg font-semibold transition-all shadow-md whitespace-nowrap cursor-pointer text-sm"
                      title="テーマと目的を削除"
                    >
                      <i className="ri-delete-bin-line"></i>
                      <span className="hidden sm:inline">削除</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleExportPDF(overview, theme)}
                    disabled={isExporting}
                    className={`flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg font-semibold transition-all shadow-md whitespace-nowrap ${isExporting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    {isExporting ? <><i className="ri-loader-4-line animate-spin"></i><span>エクスポート中...</span></> : <><i className="ri-file-download-line"></i><span>PDFダウンロード</span></>}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {[
                  { count: themeLogs.length, label: '進捗ログ' },
                  { count: themeLiteratureNotes.length, label: '参考文献' },
                  { count: themeDesigns.length, label: '分析方法' },
                  { count: themeFigures.length, label: '図表' },
                  { count: themeTermDefinitions.length, label: '用語集' },
                  { count: themeOutputs.length, label: '提出物' },
                ].map(({ count, label }) => (
                  <div key={label} className="bg-white/15 backdrop-blur-sm rounded-lg p-2 text-center">
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-teal-100">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* テーマの詳細内容 */}
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                  <i className="ri-file-text-line text-teal-600"></i>研究概要
                </h3>
                <ResearchOverview
                  overview={overview}
                  lastUpdated={overview.updated_at || overview.created_at}
                  isAdmin={isAdmin}
                  currentUser={currentUser}
                  onEdit={(item) => onEditOverview(item)}
                />
              </div>

              {themeLogs.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                    <i className="ri-bar-chart-line text-teal-600"></i>進捗ログ ({themeLogs.length}件)
                  </h3>
                  <MonthlyLogs logs={themeLogs} onUpdate={onUpdate} currentUser={currentUser} isAdmin={isAdmin} onEdit={(item) => onEditLog(item)} onDelete={onDeleteLog} />
                </div>
              )}

              {(themeLiteratureNotes.length > 0 || themeTermDefinitions.length > 0) && (
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                    <i className="ri-article-line text-emerald-600"></i>参考文献・用語集 ({themeLiteratureNotes.length + themeTermDefinitions.length}件)
                  </h3>
                  <ResearchNotes
                    literatureNotes={themeLiteratureNotes} termDefinitions={themeTermDefinitions}
                    onUpdate={onUpdate} currentUser={currentUser} isAdmin={isAdmin}
                    onEditLiterature={(item) => onEditLiterature(item)} onDeleteLiterature={onDeleteLiterature}
                    onEditTerm={(item) => onEditTerm(item)} onDeleteTerm={onDeleteTerm}
                  />
                </div>
              )}

              {themeDesigns.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                    <i className="ri-flask-line text-green-600"></i>分析方法 ({themeDesigns.length}件)
                  </h3>
                  <ResearchDesigns designs={themeDesigns} onUpdate={onUpdate} currentUser={currentUser} isAdmin={isAdmin} onEdit={(item) => onEditDesign(item)} onDelete={onDeleteDesign} />
                </div>
              )}

              {themeFigures.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                    <i className="ri-pie-chart-line text-teal-600"></i>図表 ({themeFigures.length}件)
                  </h3>
                  <FigureGallery figures={themeFigures} onUpdate={onUpdate} currentUser={currentUser} isAdmin={isAdmin} onEdit={(item) => onEditFigure(item)} onDelete={onDeleteFigure} />
                </div>
              )}

              {themeOutputs.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
                    <i className="ri-folder-download-line text-cyan-600"></i>提出物 ({themeOutputs.length}件)
                  </h3>
                  <ResearchOutputs outputs={themeOutputs} onUpdate={onUpdate} currentUser={currentUser} isAdmin={isAdmin} onEdit={(item) => onEditOutput(item)} onDelete={onDeleteOutput} />
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* 研究手順 — インライン入力フォーム */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-indigo-200 dark:border-indigo-700 overflow-hidden shadow-md">
        <button
          onClick={() => setShowInlineProcedure(prev => !prev)}
          className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-4 text-white flex items-center justify-between gap-3 cursor-pointer hover:from-indigo-700 hover:to-violet-700 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
              <i className="ri-route-line text-xl"></i>
            </div>
            <div className="text-left">
              <h3 className="text-base font-bold">研究手順を記録する</h3>
              <p className="text-indigo-100 text-xs mt-0.5">研究の具体的なステップを入力・保存できます</p>
            </div>
          </div>
          <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
            <i className={`${showInlineProcedure ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xl transition-transform`}></i>
          </div>
        </button>

        {showInlineProcedure && (
          <div className="p-5 bg-indigo-50/50 dark:bg-indigo-900/10">
            <ResearchProcedureForm
              onSuccess={() => {
                setShowInlineProcedure(false);
                onUpdate();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── メインページ ─────────────────────────────────────────────────────────────
export default function PortfolioPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [showGuide, setShowGuide] = useState(false);
  const [showPostPanel, setShowPostPanel] = useState(false);
  const [postType, setPostType] = useState<'overview' | 'procedure' | 'log' | 'note' | 'design' | 'figure' | 'term' | 'output'>('log');
  const [viewMode, setViewMode] = useState<'theme' | 'step'>('theme');
  const [filterMode, setFilterMode] = useState<'all' | 'mine'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [allOverviews, setAllOverviews] = useState<any[]>([]);
  const [myOverview, setMyOverview] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [literatureNotes, setLiteratureNotes] = useState<any[]>([]);
  const [designs, setDesigns] = useState<any[]>([]);
  const [figures, setFigures] = useState<any[]>([]);
  const [termDefinitions, setTermDefinitions] = useState<any[]>([]);
  const [outputs, setOutputs] = useState<any[]>([]);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [availableThemes, setAvailableThemes] = useState<string[]>([]);
  const [selectedTheme, setSelectedTheme] = useState('');
  const [progressRefreshKey, setProgressRefreshKey] = useState(0);

  const [editingItem, setEditingItem] = useState<any>(null);
  const [editingType, setEditingType] = useState<'overview' | 'log' | 'note' | 'design' | 'figure' | 'term' | 'output' | null>(null);

  // 研究概要削除モーダル
  const [deletingOverview, setDeletingOverview] = useState<any>(null);

  // ─── データ取得 ───────────────────────────────────────────────────────────
  const fetchAllData = async () => {
    try {
      let overviewQuery = supabase
        .from('research_overview')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterMode === 'mine' && user) overviewQuery = overviewQuery.eq('user_id', user.id);
      if (searchQuery.trim()) overviewQuery = overviewQuery.ilike('title', `%${searchQuery.trim()}%`);

      const { data: allOverviewsData } = await overviewQuery;
      setAllOverviews(allOverviewsData || []);

      const matchedThemes = (allOverviewsData || []).map((o: any) => o.title).filter(Boolean);

      if (user) {
        const { data: myOv } = await supabase
          .from('research_overview')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        setMyOverview(myOv);
      }

      if (searchQuery.trim() && matchedThemes.length === 0) {
        setLogs([]); setLiteratureNotes([]); setDesigns([]);
        setFigures([]); setTermDefinitions([]); setOutputs([]);
        setAvailableThemes([]); setRecentActivities([]);
        return;
      }

      const buildQuery = (table: string) => {
        let q = supabase.from(table).select('*').order('created_at', { ascending: false });
        if (filterMode === 'mine' && user) q = q.eq('user_id', user.id);
        if (searchQuery.trim() && matchedThemes.length > 0) q = q.in('research_theme', matchedThemes);
        return q;
      };

      const [logsRes, litRes, desRes, figRes, termRes, outRes] = await Promise.all([
        buildQuery('monthly_logs'),
        buildQuery('literature_notes'),
        buildQuery('research_designs'),
        buildQuery('research_figures'),
        buildQuery('term_definitions'),
        buildQuery('research_outputs'),
      ]);

      setLogs(logsRes.data || []);
      setLiteratureNotes(litRes.data || []);
      setDesigns(desRes.data || []);
      setFigures(figRes.data || []);
      setTermDefinitions(termRes.data || []);
      setOutputs(outRes.data || []);

      const themes = (allOverviewsData || []).map((o: any) => o.title).filter(Boolean);
      setAvailableThemes([...new Set(themes)] as string[]);

      const allActivities: any[] = [
        ...(logsRes.data || []).map((log: any) => ({ type: '進捗ログ', title: log.objective || '進捗記録', theme: log.research_theme || null, date: new Date(log.created_at).toLocaleDateString('ja-JP'), icon: 'ri-bar-chart-line', created_at: log.created_at })),
        ...(litRes.data || []).map((item: any) => ({ type: '参考文献', title: item.title || item.author || '参考文献', theme: item.research_theme || null, date: new Date(item.created_at).toLocaleDateString('ja-JP'), icon: 'ri-article-line', created_at: item.created_at })),
        ...(desRes.data || []).map((item: any) => ({ type: '分析方法', title: item.title || item.method_name || '分析方法', theme: item.research_theme || null, date: new Date(item.created_at).toLocaleDateString('ja-JP'), icon: 'ri-flask-line', created_at: item.created_at })),
        ...(figRes.data || []).map((item: any) => ({ type: '図表', title: item.title || item.caption || '図表', theme: item.research_theme || null, date: new Date(item.created_at).toLocaleDateString('ja-JP'), icon: 'ri-pie-chart-line', created_at: item.created_at })),
        ...(termRes.data || []).map((item: any) => ({ type: '用語集', title: item.term || item.title || '用語', theme: item.research_theme || null, date: new Date(item.created_at).toLocaleDateString('ja-JP'), icon: 'ri-book-mark-line', created_at: item.created_at })),
        ...(outRes.data || []).map((item: any) => ({ type: '提出物', title: item.title || '提出物', theme: item.research_theme || null, date: new Date(item.created_at).toLocaleDateString('ja-JP'), icon: 'ri-folder-download-line', created_at: item.created_at })),
      ];
      allActivities.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setRecentActivities(allActivities.slice(0, 8));
    } catch (error) {
      console.error('データ取得エラー:', error);
    }
  };

  useEffect(() => { fetchAllData(); }, [user, filterMode, searchQuery]);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) { setIsAdmin(false); return; }
      const { data } = await supabase.from('user_profiles').select('is_admin').eq('id', user.id).maybeSingle();
      setIsAdmin(data?.is_admin === true);
    };
    checkAdmin();
  }, [user]);

  // ─── 編集ハンドラー ───────────────────────────────────────────────────────
  const openEdit = (type: typeof postType, item: any) => {
    setEditingItem(item);
    setEditingType(type);
    setPostType(type);
    setShowPostPanel(true);
    setTimeout(() => document.getElementById('post-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  // ─── 削除ハンドラー ───────────────────────────────────────────────────────
  const makeDeleteHandler = (table: string) => async (id: string) => {
    try {
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      await fetchAllData();
    } catch (error) {
      console.error('削除エラー:', error);
      alert('削除に失敗しました');
    }
  };

  const handleDeleteLog = makeDeleteHandler('monthly_logs');
  const handleDeleteLiterature = makeDeleteHandler('literature_notes');
  const handleDeleteDesign = makeDeleteHandler('research_designs');
  const handleDeleteFigure = makeDeleteHandler('research_figures');
  const handleDeleteTerm = makeDeleteHandler('term_definitions');
  const handleDeleteOutput = makeDeleteHandler('research_outputs');

  // 研究概要削除
  const handleDeleteOverview = async (deleteRelated: boolean) => {
    if (!deletingOverview) return;
    try {
      // 進捗ログも削除する場合
      if (deleteRelated) {
        const theme = deletingOverview.title;
        if (theme) {
          const { error: logsError } = await supabase
            .from('monthly_logs')
            .delete()
            .eq('research_theme', theme);
          if (logsError) throw logsError;
        }
      }

      // 研究概要を削除（管理者は user_id 条件なし）
      let deleteQuery = supabase
        .from('research_overview')
        .delete()
        .eq('id', deletingOverview.id);
      if (!isAdmin) {
        deleteQuery = deleteQuery.eq('user_id', user!.id);
      }
      const { error } = await deleteQuery;
      if (error) throw error;

      setDeletingOverview(null);
      await fetchAllData();
    } catch (error) {
      console.error('研究概要削除エラー:', error);
      alert('削除に失敗しました');
    }
  };

  const handlePostSuccess = () => {
    fetchAllData();
    if (postType === 'overview') setProgressRefreshKey(prev => prev + 1);
    if (editingItem) {
      setShowPostPanel(false);
      setEditingItem(null);
      setEditingType(null);
    }
  };

  const openPostPanel = (type: typeof postType) => {
    if (!user) return;
    setPostType(type);
    setEditingItem(null);
    setEditingType(null);
    setShowPostPanel(true);
    setTimeout(() => document.getElementById('post-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getFilteredData = () => {
    if (!selectedTheme) return { logs, literatureNotes, designs, figures, termDefinitions, outputs };
    return {
      logs: logs.filter(item => item.research_theme === selectedTheme),
      literatureNotes: literatureNotes.filter(item => item.research_theme === selectedTheme),
      designs: designs.filter(item => item.research_theme === selectedTheme),
      figures: figures.filter(item => item.research_theme === selectedTheme),
      termDefinitions: termDefinitions.filter(item => item.research_theme === selectedTheme),
      outputs: outputs.filter(item => item.research_theme === selectedTheme),
    };
  };

  // ─── ローディング ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 dark:text-gray-400">読み込み中...</p>
        </div>
      </div>
    );
  }

  // ─── 未ログイン ───────────────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <Header />
        <section className="pt-20 sm:pt-24 pb-16 hero-gradient-animated hero-dark-force relative overflow-hidden flex-1 flex flex-col">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 relative flex-1 flex flex-col justify-center py-12">
            <div className="text-center mb-10">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6 border border-white/30">
                <i className="ri-file-chart-line text-4xl text-white"></i>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">研究経過報告</h1>
              <p className="text-lg text-teal-100">研究の進捗を記録し、ポートフォリオとして管理します</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate('/login')} className="px-8 py-3 bg-white text-teal-700 rounded-xl font-bold hover:bg-teal-50 transition-colors whitespace-nowrap shadow-lg cursor-pointer">
                <i className="ri-login-box-line mr-2"></i>ログイン
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ─── メイン表示 ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <Header />

      {/* マイルストーン祝福アニメーション（自分のログ数を渡す） */}
      <MilestoneCelebration logCount={filterMode === 'mine' ? logs.length : (user ? logs.filter(l => l.user_id === user.id).length : 0)} />

      {/* 研究概要削除確認モーダル */}
      {deletingOverview && (
        <DeleteOverviewModal
          overview={deletingOverview}
          onConfirm={handleDeleteOverview}
          onCancel={() => setDeletingOverview(null)}
        />
      )}

      {/* ヒーロー */}
      <div className="hero-gradient-animated hero-dark-force relative text-white py-12 sm:py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="absolute top-10 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-emerald-300/20 rounded-full blur-3xl" style={{ animation: 'float 8s ease-in-out infinite' }}></div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-8">
              <i className="ri-flask-line text-base sm:text-xl"></i>
              <span className="text-xs sm:text-sm font-semibold tracking-widest uppercase">Research Progress Archive</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">研究経過報告</h1>

            {/* スタイリッシュな説明文 */}
            <p className="text-base sm:text-lg text-white/90 mb-2 font-light tracking-wide">
              研究室に所属していなくても、独学で研究できる。
            </p>
            <p className="text-sm sm:text-base text-white/70 mb-8 font-light leading-relaxed max-w-2xl mx-auto">
              ゼミや研究室に所属している人も、自分なりに探究を進めたい人も——<br className="hidden sm:inline" />
              誰もが気軽に研究を記録・管理できる場所です。
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <button onClick={() => setShowGuide(!showGuide)} className="px-5 sm:px-6 py-2.5 sm:py-3 bg-white/20 backdrop-blur-sm text-white border border-white/40 rounded-xl font-semibold hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base">
                <i className="ri-guide-line mr-2"></i>使い方ガイド
              </button>
              <button
                onClick={() => {
                  setEditingItem(null); setEditingType(null);
                  setShowPostPanel(prev => !prev);
                  if (!showPostPanel) setTimeout(() => document.getElementById('post-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                }}
                className="px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-teal-700 rounded-xl font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base shadow-lg"
              >
                <i className="ri-add-line mr-2"></i>研究を記録する
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 flex-1">

        {/* 使い方ガイド */}
        {showGuide && (
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 border-l-4 border-teal-500 p-6 rounded-xl mb-8 shadow-sm">
            <div className="flex items-start gap-4">
              <i className="ri-information-line text-3xl text-teal-600 dark:text-teal-400 flex-shrink-0 mt-1"></i>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-3">このページの使い方</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-bold text-teal-700 dark:text-teal-300 mb-2">📝 記録の手順</h4>
                    <ol className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      <li className="flex items-start gap-2"><span className="w-5 h-5 flex items-center justify-center bg-teal-500 text-white rounded-full text-xs flex-shrink-0 mt-0.5">1</span>まず「テーマと目的」で研究の概要を登録</li>
                      <li className="flex items-start gap-2"><span className="w-5 h-5 flex items-center justify-center bg-teal-500 text-white rounded-full text-xs flex-shrink-0 mt-0.5">2</span>「研究を記録する」ボタンから投稿種別を選択</li>
                      <li className="flex items-start gap-2"><span className="w-5 h-5 flex items-center justify-center bg-teal-500 text-white rounded-full text-xs flex-shrink-0 mt-0.5">3</span>フォームに入力して保存（何度でも投稿できます）</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-teal-700 dark:text-teal-300 mb-2">🔑 権限について</h4>
                    <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                      <li className="flex items-center gap-2"><i className="ri-edit-line text-teal-500"></i>ログイン済み：投稿・自分の投稿を編集・削除</li>
                      <li className="flex items-center gap-2"><i className="ri-shield-star-line text-amber-500"></i>管理者：全投稿の編集・削除</li>
                    </ul>
                  </div>
                </div>
              </div>
              <button onClick={() => setShowGuide(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0"><i className="ri-close-line text-xl"></i></button>
            </div>
          </div>
        )}

        {/* 投稿パネル */}
        {showPostPanel && (
          <PostPanel
            postType={postType} setPostType={setPostType} editingItem={editingItem}
            onSuccess={handlePostSuccess}
            onClose={() => { setShowPostPanel(false); setEditingItem(null); setEditingType(null); }}
          />
        )}

        {/* 検索バー */}
        <section className="py-4 sm:py-5 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
          <div className="px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl">
                <i className="ri-search-line text-white text-lg"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 dark:text-white">テーマを検索</h3>
            </div>
            <div className="relative">
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="研究テーマを入力して検索..."
                className="w-full px-4 py-3 pl-12 pr-12 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-800 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
              />
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer">
                  <i className="ri-close-circle-fill text-xl"></i>
                </button>
              )}
            </div>
            {searchQuery && <p className="text-xs text-gray-500 dark:text-gray-400 mt-2"><i className="ri-information-line mr-1"></i>「{searchQuery}」を含むテーマとその関連投稿を表示中</p>}
          </div>
        </section>

        {/* 全体/自分だけフィルター */}
        <section className="py-3 sm:py-4 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm mb-6">
          <div className="px-4 sm:px-6">
            <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-full p-1">
              {(['all', 'mine'] as const).map((mode) => (
                <button key={mode} onClick={() => setFilterMode(mode)}
                  className={`px-3 sm:px-5 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${filterMode === mode ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-md' : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'}`}
                >
                  {mode === 'all' ? <><i className="ri-global-line mr-1 sm:mr-1.5"></i>全体</> : <><i className="ri-user-line mr-1 sm:mr-1.5"></i>自分だけ</>}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* 表示モード切り替え */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 mb-8 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl">
                <i className="ri-layout-grid-line text-white text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-800 dark:text-white">表示モード</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">研究の表示方法を選択できます</p>
              </div>
            </div>
            <div className="flex gap-2">
              {(['theme', 'step'] as const).map((mode) => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all cursor-pointer flex items-center gap-2 text-sm ${viewMode === mode ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-teal-400 border border-gray-300 dark:border-gray-600'}`}
                >
                  <i className={mode === 'theme' ? 'ri-folder-3-line' : 'ri-list-ordered'}></i>
                  <span className="hidden sm:inline">{mode === 'theme' ? 'テーマ別表示' : '手順別表示'}</span>
                  <span className="sm:hidden">{mode === 'theme' ? 'テーマ別' : '手順別'}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 検索0件 */}
        {searchQuery && allOverviews.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-4xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">「{searchQuery}」に一致する研究が見つかりませんでした</p>
            <button onClick={() => setSearchQuery('')} className="mt-4 px-6 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-semibold cursor-pointer">検索をクリア</button>
          </div>
        )}

        {/* 検索なし時のサマリー */}
        {!searchQuery && (
          <>
            {filterMode === 'mine' && logs.length > 0 && <MotivationBanner logs={logs} />}
            <RecentActivityTimeline activities={recentActivities} />
            <ProgressSummaryCard
              data={{ allOverviews, logs, literatureNotes, designs, figures, termDefinitions, outputs }}
              onItemClick={(type) => openPostPanel(type)}
            />
          </>
        )}

        {/* テーマ別 or 手順別 */}
        {viewMode === 'theme' ? (
          <ThemeBasedView
            allOverviews={allOverviews}
            logs={logs} literatureNotes={literatureNotes} designs={designs}
            figures={figures} termDefinitions={termDefinitions} outputs={outputs}
            currentUser={user} isAdmin={isAdmin} onUpdate={fetchAllData}
            onEditOverview={(item) => openEdit('overview', item)}
            onDeleteOverview={(item) => setDeletingOverview(item)}
            onEditLog={(item) => openEdit('log', item)} onDeleteLog={handleDeleteLog}
            onEditLiterature={(item) => openEdit('note', item)} onDeleteLiterature={handleDeleteLiterature}
            onEditDesign={(item) => openEdit('design', item)} onDeleteDesign={handleDeleteDesign}
            onEditFigure={(item) => openEdit('figure', item)} onDeleteFigure={handleDeleteFigure}
            onEditTerm={(item) => openEdit('term', item)} onDeleteTerm={handleDeleteTerm}
            onEditOutput={(item) => openEdit('output', item)} onDeleteOutput={handleDeleteOutput}
            onAddLog={() => openPostPanel('log')}
            onOpenProcedurePanel={() => {
              setPostType('procedure');
              setEditingItem(null);
              setEditingType(null);
              setShowPostPanel(true);
              setTimeout(() => document.getElementById('post-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            }}
          />
        ) : (
          <div className="space-y-8 sm:space-y-12">
            {availableThemes.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">テーマで絞り込む</p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setSelectedTheme('')} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer ${selectedTheme === '' ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-teal-400 border border-gray-300 dark:border-gray-600'}`}>すべて</button>
                  {availableThemes.map((theme, i) => (
                    <button key={i} onClick={() => setSelectedTheme(theme)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer ${selectedTheme === theme ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-teal-400 border border-gray-300 dark:border-gray-600'}`}>{theme}</button>
                  ))}
                </div>
              </div>
            )}

            {logs.length > 0 && (
              <LogThemeSummary logs={getFilteredData().logs} onThemeClick={() => openPostPanel('log')} />
            )}

            <section id="overview" className="scroll-mt-20">
              <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl shadow-lg">
                    <i className="ri-lightbulb-line text-white text-base sm:text-2xl"></i>
                  </div>
                  <h2 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">テーマと目的</h2>
                </div>
                <button onClick={() => openPostPanel('overview')} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg font-semibold hover:from-yellow-600 hover:to-orange-600 transition-all shadow-md whitespace-nowrap cursor-pointer text-xs sm:text-sm">
                  <i className="ri-add-line"></i><span>登録する</span>
                </button>
              </div>
              <ResearchOverview
                overview={myOverview}
                lastUpdated={myOverview?.updated_at || myOverview?.created_at || new Date().toISOString()}
                isAdmin={isAdmin} currentUser={user}
                onEdit={(item) => openEdit('overview', item)}
              />
            </section>

            <div className="border-t-2 border-gray-200 dark:border-gray-700"></div>

            <section id="progress" className="scroll-mt-20">
              <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl shadow-lg">
                    <i className="ri-bar-chart-line text-white text-base sm:text-2xl"></i>
                  </div>
                  <h2 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">今月の進捗</h2>
                </div>
                <button onClick={() => openPostPanel('log')} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all shadow-md whitespace-nowrap cursor-pointer text-xs sm:text-sm">
                  <i className="ri-add-line"></i><span>進捗を記録</span>
                </button>
              </div>
              <MonthlyLogs logs={getFilteredData().logs} onUpdate={fetchAllData} currentUser={user} isAdmin={isAdmin} onEdit={(item) => openEdit('log', item)} onDelete={handleDeleteLog} />
            </section>

            <div className="border-t-2 border-gray-200 dark:border-gray-700"></div>

            <section id="designs" className="scroll-mt-20">
              <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-lg">
                    <i className="ri-flask-line text-white text-base sm:text-2xl"></i>
                  </div>
                  <h2 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">分析の方法</h2>
                </div>
                <button onClick={() => openPostPanel('design')} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:from-green-600 hover:to-emerald-600 transition-all shadow-md whitespace-nowrap cursor-pointer text-xs sm:text-sm">
                  <i className="ri-add-line"></i><span>追加</span>
                </button>
              </div>
              <ResearchDesigns designs={getFilteredData().designs} onUpdate={fetchAllData} currentUser={user} isAdmin={isAdmin} onEdit={(item) => openEdit('design', item)} onDelete={handleDeleteDesign} />
            </section>

            <div className="border-t-2 border-gray-200 dark:border-gray-700"></div>

            <section id="notes" className="scroll-mt-20">
              <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl shadow-lg">
                    <i className="ri-article-line text-white text-base sm:text-2xl"></i>
                  </div>
                  <h2 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">参考文献・用語集</h2>
                </div>
                <div className="flex gap-1.5 sm:gap-2">
                  <button onClick={() => openPostPanel('note')} className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all shadow-md whitespace-nowrap cursor-pointer text-xs sm:text-sm">
                    <i className="ri-add-line"></i><span className="hidden sm:inline">参考文献</span><span className="sm:hidden">文献</span>
                  </button>
                  <button onClick={() => openPostPanel('term')} className="flex items-center gap-1 sm:gap-2 px-2.5 sm:px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all shadow-md whitespace-nowrap cursor-pointer text-xs sm:text-sm">
                    <i className="ri-add-line"></i><span>用語</span>
                  </button>
                </div>
              </div>
              <ResearchNotes
                literatureNotes={getFilteredData().literatureNotes} termDefinitions={getFilteredData().termDefinitions}
                onUpdate={fetchAllData} currentUser={user} isAdmin={isAdmin}
                onEditLiterature={(item) => openEdit('note', item)} onDeleteLiterature={handleDeleteLiterature}
                onEditTerm={(item) => openEdit('term', item)} onDeleteTerm={handleDeleteTerm}
              />
            </section>

            <div className="border-t-2 border-gray-200 dark:border-gray-700"></div>

            <section id="figures" className="scroll-mt-20">
              <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-teal-500 to-green-500 rounded-xl shadow-lg">
                    <i className="ri-pie-chart-line text-white text-base sm:text-2xl"></i>
                  </div>
                  <h2 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-teal-600 to-green-600 bg-clip-text text-transparent">図表・グラフ</h2>
                </div>
                <button onClick={() => openPostPanel('figure')} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-teal-500 to-green-500 text-white rounded-lg font-semibold hover:from-teal-600 hover:to-green-600 transition-all shadow-md whitespace-nowrap cursor-pointer text-xs sm:text-sm">
                  <i className="ri-add-line"></i><span>追加</span>
                </button>
              </div>
              <FigureGallery figures={getFilteredData().figures} onUpdate={fetchAllData} currentUser={user} isAdmin={isAdmin} onEdit={(item) => openEdit('figure', item)} onDelete={handleDeleteFigure} />
            </section>

            <div className="border-t-2 border-gray-200 dark:border-gray-700"></div>

            <section id="outputs" className="scroll-mt-20">
              <div className="flex items-center justify-between mb-4 sm:mb-6 gap-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl shadow-lg">
                    <i className="ri-folder-download-line text-white text-base sm:text-2xl"></i>
                  </div>
                  <h2 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">提出物・資料</h2>
                </div>
                <button onClick={() => openPostPanel('output')} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-lg font-semibold hover:from-cyan-600 hover:to-teal-600 transition-all shadow-md whitespace-nowrap cursor-pointer text-xs sm:text-sm">
                  <i className="ri-add-line"></i><span>追加</span>
                </button>
              </div>
              <ResearchOutputs outputs={getFilteredData().outputs} onUpdate={fetchAllData} currentUser={user} isAdmin={isAdmin} onEdit={(item) => openEdit('output', item)} onDelete={handleDeleteOutput} />
            </section>

            <div className="border-t-2 border-gray-200 dark:border-gray-700"></div>

            <section id="guide" className="scroll-mt-20">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl shadow-lg">
                  <i className="ri-guide-line text-white text-base sm:text-2xl"></i>
                </div>
                <h2 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">研究ガイド</h2>
              </div>
              <ResearchGuide key={progressRefreshKey} user={user} scrollToSection={scrollToSection} />
            </section>
          </div>
        )}
      </div>

      <Footer />

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
